import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/ApiService';
import { useToast } from '../contexts/ToastContext';
import PageHeader, { RefreshButton } from '../components/shell/PageHeader';
import { MESSAGE_STATUS, messageTypeMeta } from '../components/messages/messageContent';
import type { AdminMessageListItem, MessageStatus } from '../types/messages';
import { formatDateTime, formatRelative } from '../utils/format';
import '../styles/messages.scss';

/**
 * Comunicazioni pubblicate nella bacheca della dashboard del portale.
 * Filtri per stato (in bacheca, programmate, bozze, scadute) e origine:
 * gli avvisi generati dal sistema (Drift Monitor, Data Quality...) sono
 * distinti da quelli scritti dagli amministratori.
 */
const STATUS_FILTERS: { value: 'all' | MessageStatus; label: string }[] = [
    { value: 'all', label: 'Tutte' },
    { value: 'Published', label: 'In bacheca' },
    { value: 'Scheduled', label: 'Programmate' },
    { value: 'Draft', label: 'Bozze' },
    { value: 'Expired', label: 'Scadute' },
];

const MessagesPage: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [params, setParams] = useSearchParams();
    const [messages, setMessages] = useState<AdminMessageListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);

    const status = (params.get('status') ?? 'all') as 'all' | MessageStatus;
    const origin = params.get('origin') ?? 'admin';
    const search = params.get('q') ?? '';

    const setParam = (key: string, value: string, fallback: string) => {
        const next = new URLSearchParams(params);
        if (value === fallback) next.delete(key); else next.set(key, value);
        setParams(next, { replace: true });
    };

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setMessages(await apiService.adminGetMessages<AdminMessageListItem>());
        } catch {
            setError('Impossibile caricare le comunicazioni.');
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { void load(); }, [load]);

    const byOrigin = useMemo(
        () => messages.filter(m => origin === 'all' || (origin === 'system') === m.isSystem),
        [messages, origin]);

    const counts = useMemo(() => {
        const c: Record<string, number> = { all: byOrigin.length };
        byOrigin.forEach(m => { c[m.status] = (c[m.status] ?? 0) + 1; });
        return c;
    }, [byOrigin]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return byOrigin.filter(m =>
            (status === 'all' || m.status === status) &&
            (!q || m.title.toLowerCase().includes(q)));
    }, [byOrigin, status, search]);

    const setActive = async (m: AdminMessageListItem, active: boolean) => {
        setBusyId(m.id);
        try {
            const updated = await apiService.adminSetMessageActive<AdminMessageListItem>(m.id, active);
            setMessages(list => list.map(x => x.id === m.id ? { ...x, ...updated } : x));
            showToast(active ? 'Comunicazione pubblicata' : 'Comunicazione ritirata',
                active ? 'Torna visibile nella bacheca secondo le date impostate.' : 'Non è più visibile agli utenti.', 'success');
        } catch {
            showToast('Errore', 'Operazione non riuscita', 'danger');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <>
            <PageHeader
                title="Comunicazioni"
                description="Avvisi e novità mostrati nella bacheca della dashboard del portale, a tutti o a utenti scelti."
                actions={<>
                    <RefreshButton onClick={load} loading={loading} />
                    <Link to="/messages/new" className="btn btn-primary btn-sm">
                        <i className="bi bi-plus-lg me-1" />Nuova comunicazione
                    </Link>
                </>}
            />

            <div className="msg-toolbar">
                <div className="et-segmented" role="tablist" aria-label="Stato">
                    {STATUS_FILTERS.map(f => (
                        <button key={f.value} type="button" role="tab"
                            aria-selected={status === f.value}
                            className={status === f.value ? 'is-active' : ''}
                            onClick={() => setParam('status', f.value, 'all')}>
                            {f.label}
                            <span className="seg-count">{counts[f.value] ?? 0}</span>
                        </button>
                    ))}
                </div>
                <select className="form-select form-select-sm w-auto" aria-label="Origine"
                    value={origin} onChange={e => setParam('origin', e.target.value, 'admin')}>
                    <option value="admin">Scritte dagli admin</option>
                    <option value="system">Avvisi automatici</option>
                    <option value="all">Tutte le origini</option>
                </select>
                <div className="filter-bar__search msg-toolbar__search">
                    <i className="bi bi-search" aria-hidden="true" />
                    <input type="search" className="form-control form-control-sm" placeholder="Cerca per titolo…"
                        aria-label="Cerca comunicazioni"
                        value={search} onChange={e => setParam('q', e.target.value, '')} />
                </div>
            </div>

            {error && (
                <div className="alert alert-danger d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle me-2" /><span className="flex-grow-1">{error}</span>
                    <button className="btn btn-sm btn-outline-danger" onClick={load}>Riprova</button>
                </div>
            )}

            <div className="msg-list">
                {loading && Array.from({ length: 3 }).map((_, i) => <div key={i} className="msg-row is-skeleton" />)}

                {!loading && filtered.length === 0 && (
                    <div className="msg-empty">
                        <i className="bi bi-megaphone" />
                        <h2>{messages.length === 0 ? 'Nessuna comunicazione ancora' : 'Nessuna comunicazione con questi filtri'}</h2>
                        <p>
                            {messages.length === 0
                                ? 'Scrivi il primo avviso: comparirà nella bacheca della dashboard degli utenti.'
                                : 'Cambia stato o origine per vedere le altre.'}
                        </p>
                        {messages.length === 0 && (
                            <Link to="/messages/new" className="btn btn-primary btn-sm">
                                <i className="bi bi-plus-lg me-1" />Nuova comunicazione
                            </Link>
                        )}
                    </div>
                )}

                {!loading && filtered.map(m => {
                    const type = messageTypeMeta(m.messageType);
                    const st = MESSAGE_STATUS[m.status] ?? MESSAGE_STATUS.Published;
                    const pct = m.recipientCount > 0 ? Math.min(100, Math.round((m.readCount / m.recipientCount) * 100)) : 0;
                    return (
                        <article key={m.id} className={`msg-row is-${m.messageType.toLowerCase()}`}
                            onClick={() => navigate(`/messages/${m.id}`)} role="link" tabIndex={0}
                            onKeyDown={e => { if (e.key === 'Enter') navigate(`/messages/${m.id}`); }}>
                            <span className="msg-row__icon" aria-hidden="true"><i className={`bi ${type.icon}`} /></span>
                            <div className="msg-row__main">
                                <div className="msg-row__meta">
                                    <span>{type.label}</span>
                                    <span className={`badge ${st.badge}`}><i className={`bi ${st.icon} me-1`} />{st.label}</span>
                                    {m.isSystem && <span className="badge bg-light">Automatica</span>}
                                </div>
                                <h3>{m.title}</h3>
                                <div className="msg-row__sub">
                                    {m.status === 'Scheduled'
                                        ? <>Uscirà {formatRelative(m.publishedAt)} · {formatDateTime(m.publishedAt)}</>
                                        : <>Pubblicata {formatDateTime(m.publishedAt)}</>}
                                    {m.expiresAt && <> · scade {formatDateTime(m.expiresAt)}</>}
                                    {m.createdByEmail && <> · di {m.createdByEmail}</>}
                                </div>
                            </div>
                            <div className="msg-row__audience">
                                <span className="msg-row__who">
                                    <i className={`bi ${m.isGlobal ? 'bi-globe2' : 'bi-people'} me-1`} />
                                    {m.isGlobal ? 'Tutti gli utenti' : `${m.recipientCount} ${m.recipientCount === 1 ? 'destinatario' : 'destinatari'}`}
                                </span>
                                <div className="msg-row__reads" title={`${m.readCount} letture su ${m.recipientCount}`}>
                                    <div className="msg-row__bar"><span style={{ width: `${pct}%` }} /></div>
                                    <small>{m.readCount} {m.readCount === 1 ? 'lettura' : 'letture'}{m.recipientCount > 0 ? ` · ${pct}%` : ''}</small>
                                </div>
                            </div>
                            <div className="msg-row__actions" onClick={e => e.stopPropagation()}>
                                {m.isActive ? (
                                    <button className="btn btn-outline-secondary btn-sm" disabled={busyId === m.id}
                                        onClick={() => void setActive(m, false)} title="Togli dalla bacheca">
                                        <i className="bi bi-archive me-1" />Ritira
                                    </button>
                                ) : (
                                    <button className="btn btn-outline-secondary btn-sm" disabled={busyId === m.id}
                                        onClick={() => void setActive(m, true)} title="Rimetti in bacheca">
                                        <i className="bi bi-send me-1" />Pubblica
                                    </button>
                                )}
                                <i className="bi bi-chevron-right msg-row__chev" aria-hidden="true" />
                            </div>
                        </article>
                    );
                })}
            </div>
        </>
    );
};

export default MessagesPage;
